import { getDateRange, parseTimezone } from '@micromart/utils/lib/date'

import { getBrowserTimezone } from '@/lib/dayjs'

const PRESET_DATES = new Set([
	'today',
	'yesterday',
	'thisweek',
	'lastweek',
	'thismonth',
	'lastmonth',
	'thisyear',
	'lastyear',
	'past7days',
	'past30days',
])

const SINGLE_DAY_PRESETS = new Set(['today', 'yesterday'])

function resolveTransactionDateFilter(
	value: string | null | undefined,
	timezone?: string,
): string | undefined {
	if (!value) return undefined
	const trimmed = value.trim()
	const lower = trimmed.toLowerCase()

	if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed

	if (SINGLE_DAY_PRESETS.has(lower)) {
		const tz = parseTimezone(timezone ?? getBrowserTimezone())
		const { fromDate } = getDateRange(lower, tz)
		return fromDate.format('YYYY-MM-DD')
	}

	if (PRESET_DATES.has(lower)) return lower

	const parsed = new Date(trimmed)
	if (!Number.isNaN(parsed.getTime())) {
		return parsed.toISOString().split('T')[0]
	}

	return undefined
}

function ensureDashboardPath(pathname: string): string {
	if (pathname === '/dashboard/marketing/promotions' || pathname.startsWith('/dashboard/marketing/promotions/')) {
		return pathname.replace('/dashboard/marketing/promotions', '/dashboard/promotions')
	}
	if (pathname.startsWith('/dashboard/')) return pathname
	if (pathname === '/transactions' || pathname.startsWith('/transactions/')) {
		return `/dashboard${pathname}`
	}
	if (pathname === '/products' || pathname.startsWith('/products/')) {
		return `/dashboard${pathname}`
	}
	if (pathname === '/stores' || pathname.startsWith('/stores/')) {
		return `/dashboard${pathname}`
	}
	if (pathname === '/promotions' || pathname.startsWith('/promotions/')) {
		return `/dashboard${pathname}`
	}
	return pathname
}

function normalizeTransactionFilters(params: URLSearchParams, timezone?: string): void {
	if (params.has('status')) {
		params.set('status', params.get('status')!.toLowerCase())
	}

	if (params.has('date')) {
		const normalized = resolveTransactionDateFilter(params.get('date'), timezone)
		if (normalized) params.set('date', normalized)
		return
	}

	const from = params.get('from')
	const to = params.get('to')
	if (!from && !to) return

	const fromNorm = resolveTransactionDateFilter(from, timezone)
	const toNorm = resolveTransactionDateFilter(to, timezone)

	if (fromNorm && toNorm && fromNorm === toNorm) {
		params.set('date', fromNorm)
	} else if (fromNorm && !toNorm) {
		params.set('date', fromNorm)
	} else if (!fromNorm && toNorm) {
		params.set('date', toNorm)
	} else if (fromNorm) {
		params.set('date', fromNorm)
	}

	params.delete('from')
	params.delete('to')
}

/** Normalize assistant navigation links to valid platform filter URLs. */
export function normalizeAssistantNavigatePath(rawPath: string, timezone?: string): string {
	const trimmed = rawPath.trim()
	if (!trimmed.startsWith('/')) return trimmed

	const queryIndex = trimmed.indexOf('?')
	let pathname = ensureDashboardPath(queryIndex === -1 ? trimmed : trimmed.slice(0, queryIndex))
	const params = new URLSearchParams(queryIndex === -1 ? '' : trimmed.slice(queryIndex + 1))
	const tz = timezone ?? getBrowserTimezone()

	if (pathname.startsWith('/dashboard/transactions')) {
		normalizeTransactionFilters(params, tz)
	}

	if (pathname.startsWith('/dashboard/stores') && params.has('status')) {
		params.set('status', params.get('status')!.toLowerCase())
	}

	// AI draft links should open the planogram editor, not the first-save /new flow.
	if (/\/planogram\/\d+\/new\/?$/.test(pathname)) {
		pathname = pathname.replace(/\/new\/?$/, '')
	}

	const query = params.toString()
	return query ? `${pathname}?${query}` : pathname
}

const MARKDOWN_HREF = /\]\(([^)]+)\)/g
const FAKE_HOST_DASHBOARD_HREF = /^https?:\/\/[^/]+(\/dashboard\/[^)\s]+)$/i

/**
 * Extract an in-app dashboard path from assistant markdown hrefs.
 * Handles relative paths and model-invented hosts like https://your_vending_dashboard/dashboard/...
 */
export function extractAssistantInternalPath(href: string): string | null {
	const trimmed = href.trim()
	if (!trimmed) return null

	if (trimmed.startsWith('/')) {
		return normalizeAssistantNavigatePath(trimmed)
	}

	const fakeHostMatch = trimmed.match(FAKE_HOST_DASHBOARD_HREF)
	if (fakeHostMatch) {
		return normalizeAssistantNavigatePath(fakeHostMatch[1])
	}

	try {
		const url = new URL(trimmed)
		if (url.pathname.startsWith('/dashboard/')) {
			return normalizeAssistantNavigatePath(`${url.pathname}${url.search}`)
		}
	} catch {
		const pathMatch = trimmed.match(/(\/dashboard\/[^\s)#]+)/)
		if (pathMatch) {
			return normalizeAssistantNavigatePath(pathMatch[1])
		}
	}

	return null
}

/** Rewrite model-invented absolute dashboard URLs to relative in-app paths before markdown render. */
export function sanitizeAssistantMarkdownHrefs(content: string): string {
	return content.replace(MARKDOWN_HREF, (match, href: string) => {
		const internal = extractAssistantInternalPath(href)
		if (!internal || href.trim().startsWith('/')) return match
		return `](${internal})`
	})
}
